import { Stomp, type IMessage } from '@stomp/stompjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getWebSocketUrl } from '../../../../../../_metronic/helpers'
import { type UsuarioWithImage } from '../../../../auth'
import { addMensagem, getAtendimentoById } from '../../../atendimento/core/_requests'
import { type MensagemAtendimentoView } from '../../../atendimento/core/_models'
import { addMensagemPublica } from '../../../../tele-consulta/core/_requests'

const TEMP_MESSAGE_ID_MIN = 1000000000000
const RECONNECT_DELAY_MS = 5000

const AVISO_HISTORICO = 'Não foi possível carregar as mensagens anteriores.'
const AVISO_DESCONECTADO = 'Chat desconectado. Tentando reconectar…'
const AVISO_ENVIO = 'Sua mensagem não foi enviada. Tente novamente.'

type UseAtendimentoChatParams = {
  idAtendimento: number
  accessToken?: string
  currentUser?: UsuarioWithImage
  chatOpen: boolean
  isPaciente?: boolean
  initialMessages?: MensagemAtendimentoView[]
  protocolo?: string
}

const parseAtendimentoMessage = (body: string): MensagemAtendimentoView | null => {
  try {
    const parsed = JSON.parse(body)

    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    return parsed as MensagemAtendimentoView
  } catch {
    return null
  }
}

const getSenderKey = (msg: MensagemAtendimentoView): string | number => {
  const id = msg.usuario?.id
  return id === -1 || id === null || id === undefined ? 'paciente' : id
}

export const useAtendimentoChat = ({
  idAtendimento,
  accessToken,
  currentUser,
  chatOpen,
  isPaciente = false,
  initialMessages,
  protocolo,
}: UseAtendimentoChatParams) => {
  const [messages, setMessages] = useState<MensagemAtendimentoView[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [aviso, setAviso] = useState<string>()
  const chatOpenRef = useRef(chatOpen)

  useEffect(() => {
    chatOpenRef.current = chatOpen
  }, [chatOpen])

  useEffect(() => {
    if (isPaciente && initialMessages) {
      setMessages((prev) => {
        const tempMessages = prev.filter((msg) => msg.id && msg.id > TEMP_MESSAGE_ID_MIN)
        const activeTemp = tempMessages.filter(
          (temp) =>
            !initialMessages.some(
              (init) => init.mensagem === temp.mensagem && getSenderKey(init) === getSenderKey(temp)
            )
        )
        return [...initialMessages, ...activeTemp]
      })
    }
  }, [isPaciente, initialMessages])

  useEffect(() => {
    if (isPaciente || !accessToken) return

    let isMounted = true

    getAtendimentoById(idAtendimento)
      .then((data) => {
        if (isMounted && data?.mensagens) {
          setMessages(data.mensagens)
        }
      })
      .catch(() => {
        if (isMounted) setAviso(AVISO_HISTORICO)
      })

    return () => {
      isMounted = false
    }
  }, [idAtendimento, accessToken, isPaciente])

  useEffect(() => {
    if (chatOpen) {
      setUnreadCount(0)
    }
  }, [chatOpen])

  useEffect(() => {
    if (!accessToken) return

    const stompClient = Stomp.client(getWebSocketUrl('/socket/atendimento'))
    stompClient.debug = () => undefined
    stompClient.reconnect_delay = RECONNECT_DELAY_MS

    const onConnect = () => {
      setAviso((atual) => (atual === AVISO_DESCONECTADO ? undefined : atual))
      stompClient.subscribe(`/topic/atendimento/mensagem/${idAtendimento}`, (message: IMessage) => {
        const newMessage = parseAtendimentoMessage(message.body)

        if (!newMessage) return

        setMessages((previousMessages) => {
          if (
            newMessage.id != null &&
            previousMessages.some((previousMessage) => previousMessage.id === newMessage.id)
          ) {
            return previousMessages
          }

          const filteredMessages = previousMessages.filter((previousMessage) => {
            const isTemp = previousMessage.id && previousMessage.id > TEMP_MESSAGE_ID_MIN
            const isSameText = previousMessage.mensagem === newMessage.mensagem
            const isSameUser = getSenderKey(previousMessage) === getSenderKey(newMessage)

            return !(isTemp && isSameText && isSameUser)
          })

          if (!chatOpenRef.current) {
            setUnreadCount((count) => count + 1)
          }

          return [...filteredMessages, newMessage]
        })
      })
    }

    const onDesconectado = () => setAviso(AVISO_DESCONECTADO)
    stompClient.onWebSocketClose = onDesconectado
    stompClient.connect({ Authorization: `Bearer ${accessToken}` }, onConnect, onDesconectado)

    return () => {
      try {
        stompClient.disconnect()
      } catch {
        //
      }
    }
  }, [accessToken, idAtendimento])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return

      const tempId = Date.now()
      const tempMessage: MensagemAtendimentoView = {
        id: tempId,
        mensagem: text,
        dataFormatada: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        usuario: {
          id: isPaciente ? -1 : currentUser?.id || 0,
          nome: isPaciente ? 'Você' : currentUser?.pessoa?.nome || currentUser?.nome || 'Você',
          certificadoDigital: false,
          primeiroNome: '',
          sobreNome: '',
          cpf: '',
        },
        data: new Date().toISOString(),
      }

      setMessages((previousMessages) => [...previousMessages, tempMessage])
      setAviso((atual) => (atual === AVISO_ENVIO ? undefined : atual))

      try {
        if (isPaciente && protocolo) {
          await addMensagemPublica(protocolo, text)
        } else {
          await addMensagem(idAtendimento, text)
        }
      } catch {
        setMessages((previousMessages) =>
          previousMessages.filter((message) => message.id !== tempId)
        )
        setAviso(AVISO_ENVIO)
      }
    },
    [currentUser, idAtendimento, isPaciente, protocolo]
  )

  return {
    messages,
    unreadCount,
    sendMessage,
    aviso,
  }
}
